/**
 * Sign-in is the front door. The Clerk publishable key comes from the server
 * (`/api/config`) so one image serves both environments; until it arrives, and
 * while signed out, nothing of the board is rendered.
 */

import { ClerkProvider, Show, SignIn } from '@clerk/react';
import { useEffect, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { isStandalone } from './layout.ts';

interface GateProps {
  readonly children: ReactNode;
}

export function Gate({ children }: GateProps): JSX.Element {
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Opened from the home screen, a sign-in has to finish inside the app. The
  // Google and Apple buttons sign in by redirecting, and an iPhone sends a
  // home-screen app's redirect out to Safari, where the session is lost to the
  // app. Marking the page lets the stylesheet hide them there; email sign-in
  // runs in the page and stays.
  useEffect(() => {
    document.documentElement.classList.toggle('standalone', isStandalone());
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/config')
      .then(async (response) => {
        if (!response.ok) throw new Error(`The server answered ${response.status}`);
        const config = (await response.json()) as { clerkPublishableKey?: string };
        if (!config.clerkPublishableKey) throw new Error('The server has no sign-in configured');
        if (!cancelled) setPublishableKey(config.clerkPublishableKey);
      })
      .catch((cause: Error) => {
        if (!cancelled) setError(cause.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="gate gate--error">Cannot reach the server: {error}</p>;
  if (!publishableKey) return <p className="gate">Loading…</p>;

  return (
    <ClerkProvider publishableKey={publishableKey}>
      <Show when="signed-out">
        <div className="gate">
          <SignIn routing="hash" withSignUp />
        </div>
      </Show>
      <Show when="signed-in">{children}</Show>
    </ClerkProvider>
  );
}
